/**
 * InfinitSales - ProviderPicker (WhatsApp QR)
 *
 * Escolha do PROVEDOR (motor) da conexao por QR Code: Evolution API (padrao),
 * WAHA e Wuzapi. E uma opcao avancada: nasce RECOLHIDA atras de um link
 * discreto e so abre quando o usuario quer trocar. Usada no modal de conexao
 * do inbox e na tela de conexao do onboarding.
 *
 * Props:
 * - providers: [{id, name, description, default}] (GET /api/whatsapp/providers)
 * - current: id do provedor em uso
 * - disabled: trava enquanto recria a instancia
 * - onSelect(providerId): confirma a troca (recria a instancia e pede QR novo)
 * - disconnected: true quando o WhatsApp caiu; mostra o aviso perguntando se
 *   quer tentar outro provedor
 * - defaultOpen: abre ja expandido
 */
import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Settings2, WifiOff, RefreshCw } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

const ProviderPicker = ({
  providers = [],
  current = null,
  disabled = false,
  onSelect,
  disconnected = false,
  defaultOpen = false,
}) => {
  const { colors } = useTheme();
  const [open, setOpen] = useState(defaultOpen);
  const [choice, setChoice] = useState(null);

  const lista = useMemo(() => providers.filter((p) => p && p.id), [providers]);
  const atual = current || (lista.find((p) => p.default) || {}).id || null;
  const escolhido = choice || atual;
  const mudou = escolhido && escolhido !== atual;
  const nomeAtual = (lista.find((p) => p.id === atual) || {}).name || 'Evolution API';

  if (lista.length < 2) return null;

  const s = {
    wrap: { width: '100%', marginTop: '14px', textAlign: 'left' },
    notice: {
      display: 'flex', gap: '10px', alignItems: 'flex-start',
      padding: '10px 12px', borderRadius: '10px', marginBottom: '10px',
      background: '#fff7e6', border: '1px solid #ffd591', color: '#7a4b00', fontSize: '13px',
    },
    noticeTitle: { fontWeight: 600, marginBottom: '2px' },
    toggle: {
      display: 'inline-flex', alignItems: 'center', gap: '6px',
      background: 'transparent', border: 'none', padding: '4px 0', cursor: 'pointer',
      color: colors.textSecondary, fontSize: '12px', fontFamily: 'inherit',
    },
    panel: {
      marginTop: '8px', padding: '12px', borderRadius: '12px',
      border: `1px solid ${colors.borderColor}`, background: colors.bgSecondary,
    },
    panelTitle: { fontSize: '13px', fontWeight: 600, color: colors.textPrimary, marginBottom: '2px' },
    panelHint: { fontSize: '12px', color: colors.textSecondary, marginBottom: '10px', lineHeight: 1.4 },
    list: { display: 'flex', flexDirection: 'column', gap: '8px' },
    card: (ativo) => ({
      display: 'flex', gap: '10px', alignItems: 'flex-start', width: '100%',
      padding: '10px 12px', borderRadius: '10px', textAlign: 'left', fontFamily: 'inherit',
      border: `2px solid ${ativo ? colors.accentPrimary : colors.borderColor}`,
      background: colors.bgPrimary, color: colors.textPrimary,
      cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.7 : 1,
    }),
    radio: (ativo) => ({
      width: '16px', height: '16px', borderRadius: '50%', flexShrink: 0, marginTop: '2px',
      border: `2px solid ${ativo ? colors.accentPrimary : colors.textTertiary}`,
      background: ativo ? colors.accentPrimary : 'transparent', boxSizing: 'border-box',
    }),
    name: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: 600, flexWrap: 'wrap' },
    tag: (tipo) => ({
      fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '999px', letterSpacing: '0.02em',
      background: tipo === 'padrao' ? colors.accentPrimary : tipo === 'uso' ? colors.bgTertiary : 'transparent',
      color: tipo === 'padrao' ? '#fff' : colors.textSecondary,
      border: tipo === 'alt' ? `1px solid ${colors.borderColor}` : 'none',
    }),
    desc: { fontSize: '12px', color: colors.textSecondary, marginTop: '2px', lineHeight: 1.4 },
    actions: { display: 'flex', gap: '8px', marginTop: '10px', justifyContent: 'flex-end', flexWrap: 'wrap' },
    btnPrimary: {
      display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px',
      borderRadius: '8px', border: 'none', background: colors.accentPrimary, color: '#fff',
      fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
      opacity: !mudou || disabled ? 0.5 : 1,
    },
    btnGhost: {
      padding: '8px 12px', borderRadius: '8px', border: `1px solid ${colors.borderColor}`,
      background: 'transparent', color: colors.textPrimary, fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit',
    },
  };

  const tagDe = (p) => {
    if (p.default) return ['padrao', 'Padrão'];
    if (p.id === atual) return ['uso', 'Em uso'];
    return ['alt', 'Alternativo'];
  };

  return (
    <div style={s.wrap}>
      {disconnected && (
        <div style={s.notice}>
          <WifiOff size={18} style={{ flexShrink: 0, marginTop: '1px' }} />
          <div>
            <div style={s.noticeTitle}>Seu WhatsApp desconectou.</div>
            <div>
              Leia o QR Code para reconectar pelo provedor atual ({nomeAtual}). Se ele não estiver
              conectando ou parou de enviar, você pode tentar outro provedor.
            </div>
          </div>
        </div>
      )}

      <button type="button" style={s.toggle} onClick={() => setOpen((v) => !v)} disabled={disabled}>
        <Settings2 size={14} />
        {open ? 'Ocultar opções avançadas' : `Avançado: trocar provedor da conexão (atual: ${nomeAtual})`}
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>

      {open && (
        <div style={s.panel}>
          <div style={s.panelTitle}>Provedor da conexão</div>
          <div style={s.panelHint}>
            Todos funcionam com o mesmo inbox, agentes e automações. Na dúvida, fique no padrão.
            Ao trocar, o número é desconectado do provedor atual e você lê o QR Code de novo.
          </div>
          <div style={s.list}>
            {lista.map((p) => {
              const ativo = p.id === escolhido;
              const [tipo, rotulo] = tagDe(p);
              return (
                <button
                  key={p.id}
                  type="button"
                  style={s.card(ativo)}
                  disabled={disabled}
                  onClick={() => setChoice(p.id)}
                  aria-pressed={ativo}
                >
                  <span style={s.radio(ativo)} />
                  <span style={{ flex: 1 }}>
                    <span style={s.name}>
                      {p.name}
                      <span style={s.tag(tipo)}>{rotulo}</span>
                      {p.id === atual && !p.default ? null : null}
                    </span>
                    <div style={s.desc}>{p.description}</div>
                  </span>
                </button>
              );
            })}
          </div>
          <div style={s.actions}>
            <button type="button" style={s.btnGhost} onClick={() => { setChoice(null); setOpen(false); }} disabled={disabled}>
              Manter {nomeAtual}
            </button>
            <button
              type="button"
              style={s.btnPrimary}
              disabled={!mudou || disabled}
              onClick={() => mudou && onSelect && onSelect(escolhido)}
            >
              <RefreshCw size={14} />
              Trocar e gerar novo QR Code
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderPicker;
